import { Button, LicensePlate } from "@rumble-it/common-components/components";
import {
  FailureIcon,
  SuccessIcon,
  WaitingIcon,
} from "@/components/common/icon";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { formatDateTime } from "@/lib/helpers/format";

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return format(date, "dd/MM/yyyy");
  } catch (error) {
    console.error("Invalid date format:", error);
    return dateString;
  }
};

export const formatDossiers = (data: any[]) => {
  const imageCaptureSentStatusIcon = (item: any) => {
    const sentItems = item.imageCaptureSents || [];
    const totalCount = sentItems.length || 0;
    const completedCount = sentItems.filter(
      (imageCaptureSent: any) => imageCaptureSent.completedAt !== null,
    ).length;

    const Icon =
      totalCount > 0 && totalCount === completedCount
        ? SuccessIcon
        : FailureIcon;

    return (
      <span className="flex gap-2">
        <Icon />
        {totalCount > 0 ? `(${completedCount}/${totalCount})` : null}
      </span>
    );
  };

  type Item = {
    id: string;
    dossierType: string;
    vehicle: {
      licensePlate: {
        identifier: string;
      };
    };
    createdAt: string;
    driver: {
      person: {
        firstName: string;
        subName: string;
        lastName: string;
      };
    };
    leaseCompany: {
      name: string;
    };
    insurerName: string;
    servicePartners: {
      name: string;
    }[];
    "@type": string;
    appointmentTimeSlotsPlanned: any[];
    appointmentTimeSlots: any[];
  };

  const appointmentTimeSlotPlannedIcon = (item: Item) => {
    const appointmentTimeSlotsPlanned = item.appointmentTimeSlotsPlanned || [];
    const totalCountPlanned = appointmentTimeSlotsPlanned.length || 0;

    const appointmentTimeSlots = item.appointmentTimeSlots || [];
    const totalCount = appointmentTimeSlots.length || 0;

    const Icon =
      totalCountPlanned > 0
        ? SuccessIcon
        : totalCount > 0
          ? WaitingIcon
          : FailureIcon;

    return (
      <span className="flex gap-2">
        <Icon />
      </span>
    );
  };

  return data
    ? data.map((item: any) => ({
        id: item.id,
        dossierType: getFormattedDossierType(item.dossierType),
        licensePlate: (
          <Link to={`/dossiers/${item.id}`}>
            <LicensePlate country="NL" size="small" isLoading={false}>
              {item.vehicle.licensePlate.identifier}
            </LicensePlate>
          </Link>
        ),
        createdAt: formatDateTime(item.createdAt),
        driverName:
          item.driver?.person?.firstName +
          (item.driver?.person?.subName
            ? " " + item.driver.person.subName
            : "") +
          " " +
          item.driver?.person?.lastName,
        leaseCompanyNameOrInsurerName:
          item.leaseCompany?.name ||
          item.insuranceCompany?.name ||
          item.insurerName ||
          "-",
        servicePartner:
          (item.servicePartners &&
            (item.servicePartners?.length === 0
              ? "-"
              : item.servicePartners?.length > 1
                ? `${item.servicePartners[0].name} + ${item.servicePartners.length - 1}`
                : item.servicePartners[0].name)) ||
          "-",
        // Preferable allow the table to handle the link?
        imageCaptureSentStatus: imageCaptureSentStatusIcon(item),
        appointmentTimeSlotsPlanned: appointmentTimeSlotPlannedIcon(item),
        show: (
          <Link to={`/dossiers/${item.id}`}>
            <Button
              variant="greyLight"
              size="iconSmall"
              faIcon={{ style: "fal", name: "eye" }}
            />
          </Link>
        ),
      }))
    : null;
};

export const mapInsuranceType: Record<string, string> = {
  third_party: "WA",
  third_party_fire_theft: "WA+",
  comprehensive: "Allrisk",
  unknown: "Onbekend",
};

export const formatDossier = (data: any) => {
  return data
    ? {
        dossierType: getFormattedDossierType(data.dossierType),
        createdAt: formatDateTime(data.createdAt),
        damageAt: data.damageAt ? formatDate(data.damageAt) : null,
        causeOfDamage: getCauseOfDamage(data.causeOfDamage),
        typeOfDamage: getTypeOfDamage(data.typeOfDamage),
        damageCountry: getDamageCountry(data.damageCountry),
        damageFromDirection: getDamageFromDirection(data.damageFromDirection),
        driverType: getDriverType(data.driverType),
        paymentByType: getPaymentByType(data.paymentByType),
        damageClaimable: data.damageClaimable,
        damageReported: data.damageReported,
        insurerName: data.insuranceCompany
          ? data.insuranceCompany.name
          : data.insurerName || "",
        insurerMandateName: data.mandate || "",
        leaseCompanyName: data.leaseCompany?.name,
        pickupService: data.pickupService,
        replacementVehicle: data.replacementVehicle,
        replacementVehicleType: getReplacementVehicleType(
          data.replacementVehicleType,
        ),
        polisNumber: data.polisNumber,
        insuranceType: data.insuranceType
          ? mapInsuranceType[data.insuranceType]
          : "",
        damageNumber: data.damageNumber,
        commentFromDriver: data.commentFromDriver,
        additionalComment: data.additionalComment,
        servicePartners: data.servicePartners,
        dealershipCompany: data.dealershipCompany,
        serviceAdvisor: {
          name: data.serviceAdvisorName ?? null,
          email: data.serviceAdvisorEmail ?? null,
        },
        vehicle: {
          licensePlate: data.vehicle.licensePlate.identifier,
          brand: data.vehicle.brand,
          model: data.vehicle.model,
          year: data.vehicle.buildYear,
          fuelType: data.vehicle.fuelType,
          kilometers: data.kilometers,
          vinNumber: data.vehicle.vinNumber,
        },
        driver: {
          name:
            data.driver?.person?.firstName +
            (data.driver?.person?.subName
              ? " " + data.driver.person.subName
              : "") +
            " " +
            data.driver?.person?.lastName,
          address:
            data.driver?.address?.street +
            (data.driver?.address?.streetNumber
              ? " " + data.driver.address.streetNumber
              : "") +
            (data.driver?.address?.streetNumberAddition
              ? data.driver.address.streetNumberAddition
              : ""),
          postalCode: data.driver?.address?.postalCode,
          city: data.driver?.address?.city,
          telephone: data.driver?.person?.telephone,
          email: data.driver?.person?.email,
        },
        vehicleHandInLocationAddress: data?.vehicleHandInLocationAddress?.street
          ? {
              name: data?.vehicleHandInLocationName,
              address:
                data.vehicleHandInLocationAddress.street +
                (data.vehicleHandInLocationAddress.streetNumber
                  ? " " + data.vehicleHandInLocationAddress.streetNumber
                  : "") +
                (data.vehicleHandInLocationAddress.streetNumberAddition
                  ? data.vehicleHandInLocationAddress.streetNumberAddition
                  : ""),
              postalCode: data.vehicleHandInLocationAddress.postalCode,
              city: data.vehicleHandInLocationAddress.city,
            }
          : null,
        vehicleHandInStartDateTime: data?.vehicleHandInStartDateTime
          ? formatDateTime(data.vehicleHandInStartDateTime)
          : null,
        vehicleHandInEndDateTime: data?.vehicleHandInEndDateTime
          ? formatDateTime(data.vehicleHandInEndDateTime)
          : null,
        emailSentDriver: data?.dossierUsageEmailSentDriver
          ? data.dossierUsageEmailSentDriver.map(
              ({ emailSent }: any) => emailSent,
            )
          : null,
        emailSentServicePartner: data?.dossierUsageEmailSentServicePartner
          ? data.dossierUsageEmailSentServicePartner.map(
              ({ emailSent }: any) => emailSent,
            )
          : null,
        emailSentDealership: data?.dossierUsageEmailSentDealership
          ? data.dossierUsageEmailSentDealership.map(
              ({ emailSent }: any) => emailSent,
            )
          : null,
        smsSentDriver: data?.dossierUsageSmsSentDriver
          ? data.dossierUsageSmsSentDriver.map(({ smsSent }: any) => smsSent)
          : null,
        imageCaptureSent: data?.imageCaptureSents,
        dwProSent: data?.dossierUsageDwProSent
          ? data.dossierUsageDwProSent.map(({ dwProSent }: any) => dwProSent)
          : null,
        mediaUsages: data?.mediaUsages,
        appointmentTimeSlots: data?.appointmentTimeSlots,
        appointmentTimeSlotsPlanned: data?.appointmentTimeSlotsPlanned,
        urgent: data?.urgent ?? false,
        urgencyComment: data?.urgencyComment ?? null,
      }
    : null;
};

export const dossierTypes: Record<string, string> = {
  "repair_direct.generic": "Schadeherstel",
  "repair_direct.hail": "Hagelschade",
  "repair_direct.camper": "Camperschade",
  "repair_direct.truck": "Truckschade",
  "repair_direct.box_truck": "Bakwagenschade",
  "repair_direct.quote": "Offerte",
  "repair_direct.glass": "Glasschade",
};

export const getFormattedDossierType = (dossierType: string): string | null => {
  if (!dossierType) return null;
  return dossierTypes[dossierType] ?? dossierType;
};

const typesOfDamage: Record<string, string> = {
  dent_with_paint_damage_lt_10cm: "Deuk met lakschade (<10cm)",
  dent_without_paint_damage_gt_10cm: "Deuk zonder lakschade (>10cm)",
  dent_with_paint_damage_gt_10cm: "Deuk met lakschade (>10cm)",
  broken_or_snapped: "Gebroken/afgebroken",
  tear: "Scheur",
  interior_damage: "Interieurschade",
  paint_damage: "Lakschade",
  damage_reported_by_compound:
    "Schade geconstateerd tijdens inname auto door fleetport in Elst. Schades: Krassen op: Linker achterportier: Voorbumper: Linker achterscherm: Achterbumper:",
  license_plate_loose_and_damaged: "Nummerbord los en beschadigd",
  other: "Overig",
};

const getTypeOfDamage = (type: string): string | null => {
  if (!type) return null;
  return typesOfDamage[type] || type;
};

export const causeOfDamages: Record<string, string> = {
  animal_collision: "Aanrijding met dieren",
  blackmail: "Afdreiging",
  burglary: "Inbraak",
  burglary_with_false_key: "Inbraak met valse sleutel",
  burglary_with_true_key: "Inbraak met echte sleutel",
  burning_melting: "Schroeien/zengen/smelten",
  climbing: "Inklimming",
  collision: "Aanrijding",
  cooling_damage: "Koelschade",
  damage_vandalism: "Molest/Vandalisme",
  damage_while_parking: "Tijdens parkeren schade gereden",
  driver_changed_lanes: "Ik ben van rijbaan veranderd",
  driver_collided_rear_ended: "Achterop tegenpartij gebotst",
  driver_collided_with_cyclist_or_moped: "Aanrijding met fietser/brommer",
  driver_hit_obstacle: "Obstakel",
  driver_insufficient_lane_keeping: "Ik heb onvoldoende rechts/links gehouden",
  driver_priority_not_given: "Ik heb geen voorrang verleend",
  embezzlement: "Verduistering",
  enclosure: "Insluiting",
  explosion: "Ontploffing (koude schade)",
  external_burglary: "Inbraak",
  external_damage_while_parked: "Ik stond geparkeerd",
  external_natural_disaster: "Natuurgeweld",
  external_vandalism: "Vandalisme",
  extortion: "Afpersing",
  fallen_load: "Afgevallen lading",
  fire: "Brand",
  fraud: "Chantage",
  freezing: "Bevriezing",
  frost_damage: "Vorstschade",
  glass_breakage: "Glasbreuk",
  hail: "Hagel",
  induction: "Inductie / Overspanning",
  joyriding: "Joy-riding",
  liability: "Aansprakelijkheid",
  lightning: "Blikseminslag (koude schade)",
  loss: "Verlies",
  missing: "Vermissing",
  navigation_collision: "Aanvaring",
  object_collision: "Aanrijding met vast object",
  overturning: "Omslaan",
  partial_theft: "Diefstal partieel",
  pedestrian_collision: "Aanrijding met voetganger/fiets",
  pipe_break: "Leidingbreuk",
  pipe_leakage: "Lekkage van leidingen",
  precipitation: "Neerslag (regen, hagel, ijzel, sneeuw)",
  road_furniture_collision: "Aanrijding met wegmeubilair",
  robbery: "Beroving",
  scam: "Oplichting",
  self_defect: "Eigen gebrek",
  short_circuit: "Kortsluiting",
  slipping: "Slippen",
  smoke_and_soot: "Rook en roet",
  sneaking_in: "Insluiping",
  stone_chip: "Steenslag",
  storm: "Storm",
  theft_after_breakin: "Diefstal na (in)braak",
  theft_no_breakin: "Diefstal zonder braak",
  theft_total: "Diefstal totaal",
  theft_with_breakin: "Diefstal met braak",
  third_party_changed_lanes: "Tegenpartij rijbaan veranderd",
  third_party_hit_rear_end: "Tegenpartij achterop gebotst",
  third_party_insufficient_lane_keeping:
    "Tegenpartij heeft onvoldoende rechts/links gehouden",
  third_party_priority_not_given: "Geen voorrang verleend door tegenpartij",
  traffic: "Verkeer",
  unknown_cause: "Onbekende oorzaak",
  vandalism: "Vandalisme",
  vehicle_collision: "Aanrijding met voertuig",
  water_and_steam: "Water en stoom",
  water_entry: "Hemelwateronv. binnengedrongen",
  weather_conditions: "Weersomstandigheden",
  other: "Overige",
};

const getCauseOfDamage = (causeOfDamage: string): string | null => {
  if (!causeOfDamage) return null;
  return causeOfDamages[causeOfDamage] || causeOfDamage;
};

const getDamageFromDirection = (
  damageFromDirection?: string,
): string | null => {
  const damageFromDirections: Record<string, string> = {
    front: "Voorzijde",
    front_right: "Voorzijde rechts",
    right: "Zijkant rechts",
    roof: "Dak",
    rear_right: "Achterzijde rechts",
    rear: "Achterzijde",
    rear_left: "Achterzijde links",
    front_left: "Voorzijde links",
    left: "Zijkant links",
  };

  if (!damageFromDirection) return null;
  return damageFromDirections[damageFromDirection] || damageFromDirection;
};

const getDamageCountry = (countryIdentifier?: string): string | null => {
  const countries: Record<string, string> = {
    NL: "Nederland",
    BE: "België",
  };

  if (!countryIdentifier) return null;
  return countries[countryIdentifier] || null;
};

const getReplacementVehicleType = (
  replacementVehicleType?: string,
): string | null => {
  const replacementVehicleTypes: Record<string, string> = {
    car: "Auto",
    bike: "Fiets",
    pickup: "Haal & brengservice",
    none: "Geen vervangend voertuig",
  };

  if (!replacementVehicleType) return null;
  return (
    replacementVehicleTypes[replacementVehicleType] || replacementVehicleType
  );
};

const getDriverType = (driverType: string): string | null => {
  const driverTypes: Record<string, string> = {
    lease: "Lease",
    private: "Privé",
    business: "Zakelijk",
  };

  if (!driverType) return null;
  return driverTypes[driverType] || driverType;
};

const getPaymentByType = (paymentByType?: string): string | null => {
  const paymentByTypes: Record<string, string> = {
    self: "Zelf betalen",
    insurance: "Verzekering",
    invoice_by_dealership: "Factuur via dealer",
  };

  if (!paymentByType) return null;
  return paymentByTypes[paymentByType] || paymentByType;
};

/**
 * Reads the raw backing value of a backend enum field.
 *
 * Enums registered in the backend's `BackedEnumTranslatable` normalizer serialize
 * as `{ name, value, label }` instead of a bare string. This keeps comparisons
 * against the raw value working for both shapes.
 */
export const getEnumValue = (
  field?: string | { value: string } | null,
): string | null => {
  if (!field) return null;
  return typeof field === "string" ? field : field.value;
};

export const sources: Record<string, string> = {
  "dealer-intake": "Dealer Intake",
  chatbot: "Chatbot",
  widget: "Widget",
  "mobile-app": "Mobiele app",
  website: "Website",
  "": "Onbekend",
};

export const preferred: Record<string, string> = {
  not_preferred: "Agreed partner",
  preferred: "Preferred partner",
};
